import { useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, CircularProgress, Collapse, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, ChevronDown, Circle, Pencil, X } from "lucide-react";

import { AlertNote } from "@/components/AlertNote";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { brand } from "@/theme/theme";
import {
  STEP_KEYS,
  engineSubtitle,
  engineTitle,
  extractHost,
  isAiEngine,
  listSx,
  totalKeywordUnits,
} from "../seoSetupUtils";
import { CountryFlag } from "@/components/CountryFlag";
import { EngineIcon, SUCCESS, StepIntro } from "../components";

function Section({ title, onEdit, children }) {
  return (
    <Box sx={{ borderRadius: "12px", border: "1px solid #E8ECF2", backgroundColor: "#fff", overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1,
          backgroundColor: "#F8FAFC",
          borderBottom: "1px solid #E8ECF2",
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Button size="xs" variant="ghost" onClick={onEdit}>
          <Pencil size={12} />
          Editar
        </Button>
      </Box>
      <Box sx={{ px: 2, py: 1.5 }}>{children}</Box>
    </Box>
  );
}

Section.propTypes = { title: PropTypes.string.isRequired, onEdit: PropTypes.func.isRequired, children: PropTypes.node };

/** `removals` (edit only): what the edit deletes, from `diffRemovals`. */
export function ReviewSummary({ draft, clientId, onEditStep, serverError, isEdit = false, removals = null }) {
  const queryClient = useQueryClient();
  const units = totalKeywordUnits(draft.engines, draft.keywords);
  const edit = (key) => () => onEditStep(STEP_KEYS.indexOf(key));
  const connections = [
    ["ga4", "Google Analytics 4"],
    ["search_console", "Search Console"],
  ];

  return (
    <Stack spacing={2}>
      <StepIntro
        title={isEdit ? "Revisar y guardar" : "Revisar y crear"}
        description={
          isEdit
            ? "Comprueba los cambios. Lo nuevo empezará a recopilar datos en unas 24 horas."
            : "Comprueba la configuración. Al crear el proyecto empezaremos a recopilar datos; los primeros llegan en unas 24 horas."
        }
      />
      {serverError && <AlertNote variant="danger">{serverError}</AlertNote>}
      {removals && (removals.engines.length > 0 || removals.keywords > 0 || removals.competitors.length > 0) && (
        <AlertNote variant="warning">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Al guardar se eliminará:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {removals.engines.map((engine) => (
              <li key={engine.key}>
                <Typography variant="body2">{`${engineTitle(engine)} (${engineSubtitle(engine)}) y todas sus keywords`}</Typography>
              </li>
            ))}
            {removals.keywords > 0 && (
              <li>
                <Typography variant="body2">
                  {`${removals.keywords} keyword${removals.keywords === 1 ? "" : "s"} en total`}
                </Typography>
              </li>
            )}
            {removals.competitors.length > 0 && (
              <li>
                <Typography variant="body2">{`Competidores: ${removals.competitors.join(", ")}`}</Typography>
              </li>
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            El histórico de posiciones de lo eliminado se pierde y no se puede recuperar.
          </Typography>
        </AlertNote>
      )}

      <Section title="Sitio web" onEdit={edit("general")}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {draft.general.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {extractHost(draft.general.url)} · dominio completo con subdominios
        </Typography>
      </Section>

      <Section title={`Motores y keywords · ${units} en total`} onEdit={edit("engines")}>
        <Stack spacing={1.25}>
          {draft.engines.map((engine) => {
            const list = draft.keywords[engine.key] || [];
            return (
              <Box key={engine.key} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
                <EngineIcon family={engine.family} size={28} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {engineTitle(engine)}
                    </Typography>
                    <Badge color="muted" dot={false}>
                      {isAiEngine(engine) ? `IA · marca «${draft.brand}»` : `${list.length} keywords`}
                    </Badge>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                  >
                    <CountryFlag code={engine.country} width={14} />
                    {engineSubtitle(engine)}
                  </Typography>
                  {list.length > 0 && (
                    <Typography variant="caption" sx={{ display: "block", color: "#475569", mt: 0.25 }}>
                      {list.slice(0, 6).join(", ")}
                      {list.length > 6 ? ` y ${list.length - 6} más` : ""}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Stack>
      </Section>

      <Section title={`Competidores · ${draft.competitors.length}`} onEdit={edit("competitors")}>
        {draft.competitors.length ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {draft.competitors.map((domain) => (
              <Badge key={domain} color="muted" dot={false}>
                {domain}
              </Badge>
            ))}
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            Sin competidores. Podrás añadirlos más adelante.
          </Typography>
        )}
      </Section>

      <Section title="Analítica" onEdit={edit("analytics")}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {connections.map(([key, label]) => {
            const connected = Boolean(queryClient.getQueryData(["client-connection", key, clientId]));
            return (
              <Badge key={key} color={connected ? "done" : "muted"}>
                {label} {connected ? "conectada" : "sin conectar"}
              </Badge>
            );
          })}
        </Box>
      </Section>
    </Stack>
  );
}

ReviewSummary.propTypes = {
  draft: PropTypes.object.isRequired,
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onEditStep: PropTypes.func.isRequired,
  serverError: PropTypes.string,
  isEdit: PropTypes.bool,
  removals: PropTypes.shape({
    engines: PropTypes.arrayOf(PropTypes.object),
    keywords: PropTypes.number,
    competitors: PropTypes.arrayOf(PropTypes.string),
  }),
};

// `count` names the `result.counts` entry shown as "added/requested".
const CHECKLIST = [
  { key: "site", label: "Proyecto creado" },
  { key: "engines", label: "Motores de búsqueda", count: "engines" },
  { key: "keywords", label: "Keywords", count: "keywords" },
  { key: "competitors", label: "Competidores", count: "competitors" },
  { key: "check", label: "Primer chequeo de posiciones solicitado" },
];

const STATE_META = {
  done: { color: SUCCESS, background: "#E3F6EA", Icon: Check },
  partial: { color: "#FF7A00", background: "#FFE1CE", Icon: AlertTriangle },
  error: { color: "#DC2626", background: "#FEE2E2", Icon: X },
  pending: { color: "#94A3B8", background: "#F1F5F9", Icon: Circle },
};

const STEP_STATES = { done: "done", ok: "done", skipped: "done", partial: "partial", error: "error", failed: "error" };
const stepState = (value) => STEP_STATES[value] || "pending";

/** Checklist of the provisioning request, plus what could not be added once it ends. */
export function ProvisionProgress({ isRunning, result, failure, engines, isEdit = false }) {
  const [showFailed, setShowFailed] = useState(true);
  const steps = result?.steps || {};
  const counts = result?.counts || {};
  const failed = result?.failed || {};
  const status = failure ? "error" : result?.status;

  const engineLabel = (key) => {
    const engine = engines?.find((e) => e.key === key);
    return engine ? `${engineTitle(engine)} (${engineSubtitle(engine)})` : key;
  };
  const failedGroups = [
    { title: "Motores", lines: (failed.engines || []).map(engineLabel) },
    ...Object.entries(failed.keywords || {})
      .filter(([, list]) => list?.length)
      .map(([key, list]) => ({ title: `Keywords · ${engineLabel(key)}`, lines: [list.join(", ")] })),
    { title: "Competidores", lines: failed.competitors?.length ? [failed.competitors.join(", ")] : [] },
    {
      title: "Sin quitar",
      lines: failed.removals ? [`${failed.removals} elemento${failed.removals === 1 ? "" : "s"} que había que eliminar`] : [],
    },
    { title: "Nombre del proyecto", lines: failed.title ? ["No se ha podido cambiar"] : [] },
  ].filter((group) => group.lines.length);
  // An edit never creates the project, so its row would only say "skipped".
  const checklist = isEdit ? CHECKLIST.filter((row) => row.key !== "site") : CHECKLIST;

  const firstPending = Math.max(checklist.findIndex((row) => stepState(steps[row.key]) === "pending"), 0);

  let banner = null;
  if (isRunning) {
    banner = (
      <AlertNote variant="info">
        {isEdit ? "Estamos aplicando los cambios." : "Estamos creando el proyecto."} Suele tardar menos de un
        minuto; no cierres esta ventana hasta que termine.
      </AlertNote>
    );
  } else if (status === "active") {
    banner = (
      <Alert severity="success" sx={{ borderRadius: "8px" }}>
        {result?.message || "Proyecto creado. Los primeros datos estarán disponibles en unas 24 horas."}
      </Alert>
    );
  } else if (status === "partial") {
    banner = (
      <AlertNote variant="warning">
        {result?.message || "El proyecto se ha creado, pero no se ha podido completar todo."}
      </AlertNote>
    );
  } else if (status === "error") {
    banner = <AlertNote variant="danger">{failure || result?.message || "No se ha podido crear el proyecto."}</AlertNote>;
  }

  return (
    <Stack spacing={2}>
      <StepIntro
        title={
          isRunning
            ? isEdit
              ? "Guardando cambios…"
              : "Creando el proyecto…"
            : status === "error"
              ? "No se ha completado"
              : "Configuración terminada"
        }
      />
      {banner}

      <Box component="ul" sx={{ ...listSx, px: 2, py: 0.5, overflow: "visible", backgroundColor: "#fff" }}>
        {checklist.map((row, index) => {
          let state = isRunning ? "pending" : stepState(steps[row.key]);
          if (!isRunning && failure && state === "pending") state = "error";
          const running = isRunning && index === firstPending;
          const meta = STATE_META[state];
          const count = counts[row.count];
          const detail = row.count && Array.isArray(count) && count.length === 2 ? `${count[0]}/${count[1]}` : "";
          return (
            <Box component="li" key={row.key} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
              <Box
                sx={{
                  width: 26,
                  height: 26,
                  minWidth: 26,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: meta.background,
                  color: meta.color,
                }}
              >
                {running ? <CircularProgress size={14} sx={{ color: brand.ink }} /> : <meta.Icon size={14} strokeWidth={3} />}
              </Box>
              <Typography
                variant="body2"
                sx={{ flex: 1, fontWeight: 600, color: state === "pending" && !running ? "text.secondary" : "text.primary" }}
              >
                {row.label}
              </Typography>
              {detail && (
                <Typography variant="caption" sx={{ color: meta.color, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                  {detail}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      {!isRunning && failedGroups.length > 0 && (
        <Box sx={{ borderRadius: "12px", border: "1px solid #FFE1CE", backgroundColor: "#FFFAF5" }}>
          <Box
            component="button"
            type="button"
            onClick={() => setShowFailed((prev) => !prev)}
            aria-expanded={showFailed}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 2,
              py: 1.25,
              border: "none",
              background: "transparent",
              font: "inherit",
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#B45309" }}>
              {isEdit ? "Lo que no se ha podido aplicar" : "Lo que no se ha podido añadir"}
            </Typography>
            <ChevronDown
              size={18}
              style={{ transform: showFailed ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }}
            />
          </Box>
          <Collapse in={showFailed}>
            <Stack spacing={1.25} sx={{ px: 2, pb: 1.75 }}>
              {failedGroups.map((group) => (
                <Box key={group.title}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {group.title}
                  </Typography>
                  {group.lines.map((line, i) => (
                    <Typography key={i} variant="body2" sx={{ color: "#475569" }}>
                      {line}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Stack>
  );
}

ProvisionProgress.propTypes = {
  isRunning: PropTypes.bool,
  result: PropTypes.object,
  failure: PropTypes.string,
  engines: PropTypes.arrayOf(PropTypes.object),
  isEdit: PropTypes.bool,
};
